import React, { useState, useEffect } from 'react';
import ConfirmDialog from './ConfirmDialog';
import EditNewsModal from './EditNewsModal';
import AddNewsModal from './AddNewsModal';
import ImportNewsModal from './ImportNewsModal';
import type { NewsFormData } from './EditNewsModal';
import { API_CONFIG } from '../../config/api';

export enum NewsState {
  Approved = "Approved",
  Pending = "Pending",
  Rejected = "Rejected"
}

interface News {
  id: number;
  mainTopic: string;
  additionalTopicsEn: string | string[];
  additionalTopicsDe: string | string[];
  title: string;
  summary: string;
  imageUrl: string;
  link: string;
  language: string;
  translatedTitle: string | null;
  translatedSummary: string | null;
  state: NewsState;
  publishedAt: string;
}

interface ConfirmAction {
  type: 'approve' | 'reject';
  newsId: number;
  newsTitle: string;
}

interface FilterState {
  state: NewsState | 'All';
  topic: string;
  sortByDateDesc: boolean;
}

const styles = `
  .topic-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .main-topic {
    font-weight: 600;
    color: #333;
  }

  .additional-topics {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .topic-chip {
    background-color: #e0e0e0;
    color: #666;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    white-space: nowrap;
  }

  .bulk-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
  }

  .select-all {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .bulk-buttons {
    display: flex;
    gap: 1rem;
  }

  .bulk-buttons button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }

  .bulk-buttons .approve {
    background-color: #28a745;
    color: white;
  }

  .bulk-buttons .reject {
    background-color: #dc3545;
    color: white;
  }

  .bulk-buttons button:hover {
    opacity: 0.9;
  }

  .import-news-button {
    background-color: #17a2b8;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-weight: 500;
    margin-right: 0.5rem;
  }

  .import-news-button:hover {
    background-color: #138496;
  }
`;

const NewsManagement = () => {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPending, setShowPending] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<FilterState>({
    state: NewsState.Pending,
    topic: '',
    sortByDateDesc: true
  });
  const [topics, setTopics] = useState<string[]>([]);

  useEffect(() => {
    fetchNews();
  }, [filters.state, filters.topic, filters.sortByDateDesc]);

  const fetchNews = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.state !== 'All') {
        params.append('state', filters.state);
      }
      if (filters.topic) {
        params.append('topic', filters.topic);
      }
      params.append('sortByDateDesc', filters.sortByDateDesc.toString());

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEWS.BASE}?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }
      const data = await response.json();

      // Handle the response data structure
      if (Array.isArray(data)) {
        // If the response is directly an array of news items
        setNews(data);
        // Extract unique topics from the news items
        const uniqueTopics = Array.from(new Set(data.map(item => item.mainTopic)));
        setTopics(uniqueTopics);
      } else if (data.news && Array.isArray(data.news)) {
        // If the response has a news property containing the array
        setNews(data.news);
        setTopics(data.topics || []);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError('Failed to load news');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    // Update filters state
    setFilters(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Use the new value directly instead of accessing from filters
    const params = new URLSearchParams();
    if (name === 'state' && newValue !== 'All') {
      params.append('state', newValue as string);
    } else if (name === 'state') {
      params.append('state', filters.state);
    }

    if (name === 'topic' && newValue) {
      params.append('topic', newValue as string);
    } else if (name === 'topic') {
      params.append('topic', filters.topic);
    }

    params.append('sortByDateDesc', name === 'sortByDateDesc' ? newValue.toString() : filters.sortByDateDesc.toString());

    fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEWS.BASE}?${params.toString()}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch news');
        }
        return response.json();
      })
      .then(data => {
        if (Array.isArray(data)) {
          setNews(data);
          const uniqueTopics = Array.from(new Set(data.map(item => item.mainTopic)));
          setTopics(uniqueTopics);
        } else if (data.news && Array.isArray(data.news)) {
          setNews(data.news);
          setTopics(data.topics || []);
        } else {
          throw new Error('Invalid response format');
        }
      })
      .catch(err => {
        setError('Failed to load news');
        console.error('Error:', err);
      });
  };

  const handleAction = async (id: number, action: 'edit' | 'approve' | 'reject') => {
    if (action === 'edit') {
      const newsToEdit = news.find(item => item.id === id);
      if (newsToEdit) {
        setEditingNews(newsToEdit);
      }
    } else {
      const newsItem = news.find(item => item.id === id);
      if (newsItem) {
        setConfirmAction({
          type: action,
          newsId: id,
          newsTitle: newsItem.title
        });
      }
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      const { type, newsId } = confirmAction;
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEWS.TOGGLE(newsId.toString(), type)}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Failed to ${type} news`);
      }

      await fetchNews();
    } catch (err) {
      setError(`Failed to ${confirmAction.type} news`);
      console.error('Error:', err);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleSaveEdit = async (formData: NewsFormData) => {
    if (!editingNews) return;

    try {
      // Convert additionalTopics strings to arrays
      const formattedData = {
        ...formData,
        additionalTopicsEn: formData.additionalTopicsEn ? formData.additionalTopicsEn.split(',').map(topic => topic.trim()) : [],
        additionalTopicsDe: formData.additionalTopicsDe ? formData.additionalTopicsDe.split(',').map(topic => topic.trim()) : []
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEWS.EDIT(editingNews.id.toString())}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        throw new Error('Failed to update news');
      }

      await fetchNews();
      setEditingNews(null);
    } catch (err) {
      setError('Failed to update news');
      console.error('Error:', err);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = news.map(item => item.id);
      setSelectedNews(new Set(allIds));
    } else {
      setSelectedNews(new Set());
    }
  };

  const handleSelectNews = (id: number) => {
    const newSelected = new Set(selectedNews);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedNews(newSelected);
  };

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    if (selectedNews.size === 0) return;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEWS[action === 'approve' ? 'BULK_APPROVE' : 'BULK_REJECT']}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newsIds: Array.from(selectedNews)
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} news`);
      }

      await fetchNews();
      setSelectedNews(new Set());
    } catch (err) {
      setError(`Failed to ${action} news`);
      console.error('Error:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading news...</div>;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="content-section">
        <div className="content-header">
          <div>
            <h2>News Management</h2>
            <p>Manage and moderate news articles</p>
          </div>
          <div className="header-actions">
            <div className="news-filter-buttons">
              <button
                className={`filter-button ${!showPending ? 'active' : ''}`}
                onClick={() => setShowPending(false)}
              >
                All News
              </button>
              <button
                className={`filter-button ${showPending ? 'active' : ''}`}
                onClick={() => setShowPending(true)}
              >
                Pending News
              </button>
            </div>
            <button
              className="import-news-button"
              onClick={() => setIsImportModalOpen(true)}
            >
              Import News
            </button>
            <button
              className="add-news-button"
              onClick={() => setIsAddModalOpen(true)}
            >
              Add News
            </button>
          </div>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="state">State:</label>
            <select
              id="state"
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
            >
              <option value="All">All States</option>
              <option value={NewsState.Approved}>Approved</option>
              <option value={NewsState.Pending}>Pending</option>
              <option value={NewsState.Rejected}>Rejected</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="topic">Topic:</label>
            <select
              id="topic"
              name="topic"
              value={filters.topic}
              onChange={handleFilterChange}
            >
              <option value="">All Topics</option>
              {topics.map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>
              <input
                type="checkbox"
                name="sortByDateDesc"
                checked={filters.sortByDateDesc}
                onChange={handleFilterChange}
              />
              Sort by Latest First
            </label>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="news-table-container">
          <div className="bulk-actions">
            <div className="select-all">
              <input
                type="checkbox"
                checked={selectedNews.size === news.length}
                onChange={handleSelectAll}
              />
              <span>Select All</span>
            </div>
            {selectedNews.size > 0 && (
              <div className="bulk-buttons">
                <button
                  className="action-btn approve"
                  onClick={() => handleBulkAction('approve')}
                >
                  Approve Selected ({selectedNews.size})
                </button>
                <button
                  className="action-btn reject"
                  onClick={() => handleBulkAction('reject')}
                >
                  Reject Selected ({selectedNews.size})
                </button>
              </div>
            )}
          </div>
          <table className="news-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedNews.size === news.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Topic</th>
                <th>Title</th>
                <th>Summary</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {news.map(item => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedNews.has(item.id)}
                      onChange={() => handleSelectNews(item.id)}
                    />
                  </td>
                  <td>
                    <div className="topic-container">
                      <div className="main-topic">{item.mainTopic}</div>
                      {item.additionalTopicsEn && (
                        <div className="additional-topics">
                          {(Array.isArray(item.additionalTopicsEn)
                            ? item.additionalTopicsEn
                            : item.additionalTopicsEn.split(',')
                          ).map((topic, index) => (
                            <span key={index} className="topic-chip">
                              {typeof topic === 'string' ? topic.trim() : topic}
                            </span>
                          ))}
                        </div>
                      )}
                      {item.additionalTopicsDe && (
                        <div className="additional-topics">
                          {(Array.isArray(item.additionalTopicsDe)
                            ? item.additionalTopicsDe
                            : item.additionalTopicsDe.split(',')
                          ).map((topic, index) => (
                            <span key={index} className="topic-chip">
                              {typeof topic === 'string' ? topic.trim() : topic}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="news-title">
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="title-link"
                      >
                        {item.title}
                      </a>
                      {item.translatedTitle && (
                        <div className="translated-text">{item.translatedTitle}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="news-summary">
                      <div>{item.summary}</div>
                      {item.translatedSummary && (
                        <div className="translated-text">{item.translatedSummary}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="status-column">
                      <div className="image-preview" onClick={() => setSelectedImage(item.imageUrl)}>
                        <img src={item.imageUrl} alt="News thumbnail" />
                      </div>
                      <span className={`state-badge ${item.state.toLowerCase()}`}>
                        {item.state}
                      </span>
                      <div className="published-date">
                        {new Date(item.publishedAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        onClick={() => handleAction(item.id, 'edit')}
                      >
                        Edit
                      </button>
                      {item.state === NewsState.Pending && (
                        <>
                          <button
                            className="action-btn approve"
                            onClick={() => handleAction(item.id, 'approve')}
                          >
                            Approve
                          </button>
                          <button
                            className="action-btn reject"
                            onClick={() => handleAction(item.id, 'reject')}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ConfirmDialog
          isOpen={!!confirmAction}
          title={`Confirm ${confirmAction?.type === 'approve' ? 'Approval' : 'Rejection'}`}
          message={`Are you sure you want to ${confirmAction?.type} the news article "${confirmAction?.newsTitle}"?`}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
          confirmText={confirmAction?.type === 'approve' ? 'Approve' : 'Reject'}
          type={confirmAction?.type}
        />

        <EditNewsModal
          isOpen={!!editingNews}
          onClose={() => setEditingNews(null)}
          onSave={handleSaveEdit}
          news={editingNews}
        />

        <AddNewsModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={fetchNews}
        />

        <ImportNewsModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onSave={fetchNews}
        />

        {selectedImage && (
          <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
            <div className="image-modal-content" onClick={e => e.stopPropagation()}>
              <img src={selectedImage} alt="News full size" />
              <button className="close-image-modal" onClick={() => setSelectedImage(null)}>
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NewsManagement;